import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#E3DED4] rounded-lg px-4 py-3 text-sm text-[#142D44] placeholder:text-[#A8B0BC] focus:outline-none focus:border-[#B85D2A] transition-colors duration-200";

export default function Contact() {
  const { t, lang } = useLanguage();
  const f = t.contact.form;
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service_type: "", timeline: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.service_type || !form.message) {
      toast.error(f.required);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, language: lang });
      setSuccess(true);
      toast.success(f.success);
      setForm({ full_name: "", email: "", phone: "", service_type: "", timeline: "", message: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-[72px]">
      <Seo page="contact" path="/contact" />
      <section className="py-20 sm:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold mb-6">{t.contact.eyebrow}</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#142D44] leading-[1.1]">
              <MaskedLines lines={[t.contact.title]} />
            </h1>
            <p className="mt-6 text-[#4A5568] leading-relaxed">{t.contact.sub}</p>

            <Reveal delay={0.2} className="mt-12 bg-[#F4F1EA] border border-[#E3DED4] rounded-lg p-7 space-y-5">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#8C6B4F] font-semibold">{t.contact.directTitle}</p>
              <a href="mailto:corporate@horizoncy.com" className="flex items-center gap-3 text-sm text-[#142D44] hover:text-[#B85D2A] transition-colors" data-testid="contact-email-link">
                <Mail size={17} className="text-[#B85D2A]" strokeWidth={1.5} /> corporate@horizoncy.com
              </a>
              <a href="tel:+35799470963" className="flex items-center gap-3 text-sm text-[#142D44] hover:text-[#B85D2A] transition-colors" data-testid="contact-phone-link">
                <Phone size={17} className="text-[#B85D2A]" strokeWidth={1.5} /> +357 99 470 963
              </a>
              <a href="https://wa.me/35799470963" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-[#142D44] hover:text-[#B85D2A] transition-colors" data-testid="contact-whatsapp-link">
                <MessageCircle size={17} className="text-[#B85D2A]" strokeWidth={1.5} /> {t.contact.whatsapp}
              </a>
              <p className="flex items-center gap-3 text-sm text-[#4A5568]">
                <MapPin size={17} className="text-[#B85D2A]" strokeWidth={1.5} /> {t.contact.location}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            {success && (
              <div className="mb-6 flex items-start gap-3 bg-[#EEF3EC] border border-[#4A5D4E]/30 rounded-lg px-5 py-4" data-testid="contact-success-alert">
                <CheckCircle2 size={18} className="text-[#4A5D4E] mt-0.5 shrink-0" />
                <p className="text-sm text-[#2F3E33]">{f.success}</p>
              </div>
            )}
            <form onSubmit={submit} className="bg-white border border-[#E3DED4] rounded-lg p-7 sm:p-9 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-[#526071] mb-2">{f.name} *</label>
                  <input data-testid="contact-name-input" className={inputCls} placeholder={f.namePh} value={form.full_name} onChange={set("full_name")} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#526071] mb-2">{f.email} *</label>
                  <input data-testid="contact-email-input" type="email" className={inputCls} placeholder={f.emailPh} value={form.email} onChange={set("email")} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#526071] mb-2">{f.phone}</label>
                <input data-testid="contact-phone-input" className={inputCls} placeholder={f.phonePh} value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#526071] mb-2">{f.service} *</label>
                <select data-testid="contact-service-select" className={inputCls} value={form.service_type} onChange={set("service_type")}>
                  <option value="">—</option>
                  {f.serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#526071] mb-2">{f.timeline}</label>
                <select data-testid="contact-timeline-select" className={inputCls} value={form.timeline} onChange={set("timeline")}>
                  <option value="">—</option>
                  {f.timelineOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#526071] mb-2">{f.message} *</label>
                <textarea data-testid="contact-message-input" rows={5} className={inputCls} placeholder={f.messagePh} value={form.message} onChange={set("message")} />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center px-7 py-4 rounded-lg bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? f.submitting : f.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 bg-[#F4F1EA] border-t border-[#E3DED4]" data-testid="contact-faq-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold">{t.faq.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#142D44]">{t.faq.title}</h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              {t.faq.list.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-[#E3DED4]">
                  <AccordionTrigger className="text-left font-serif text-lg sm:text-xl text-[#142D44] hover:text-[#B85D2A] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A5568] leading-relaxed text-base pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
